import { InfaceAuthError } from '@inface-sdk/web-auth'
import { ToyPCSdk } from '../../sdks'
import { GW_PATH, CONSOLE_TYPE } from '../../constants'
import { URI } from '../../utils/common'

export default class ConsoleLinking extends ToyPCSdk {
  constructor({ infaceAuth, queryParams }) {
    super(queryParams)

    this.infaceAuth = infaceAuth
    this.type = queryParams.console_type
    this.token = queryParams.console_token
    this.linkTicket = queryParams.link_ticket
    this.redirectUrl = queryParams.redirect_url
  }

  /**
   * IAL 콘솔 링킹에서 Xbox, PSN 등 콘솔이 아닌 PC를 통해 웹으로 접근하는 경우,
   * 링킹 완료 이후 웹에서 PC SDK로 루프백을 통해 콜백 메세지를 전달하게 됩니다.
   *
   * (25. 08. 05 기준) PC 기반으로 접근하는 타입은 아래와 같습니다.
   * - Steam
   * - Epic Games
   * - Xbox (wingdk 케이스이며, 일반 Xbox 콘솔에서 접근하는 경우에는 해당되지 않습니다.)
   */
  get isCallbackRequired() {
    const targetConsoleTypeList = [CONSOLE_TYPE.STEAM, CONSOLE_TYPE.EPIC_GAMES]
    const isTargetConsoleType = targetConsoleTypeList.includes(this.type?.toLowerCase())

    /**
     * wingdk 케이스인 경우 SDK에서 아래와 같이 접근합니다.
     * 이 경우 Steam/EpicGames와 동일하게 success 페이지로 이동 및 루프백으로 성공/실패를 전달합니다.
     * - console_type: xbox
     * - redirect_url: https://signin.nexon.com/pages/link/ko/check?ticket=___
     */
    const { uri: processedRedirectUri } = URI.validateAndProcessUri(this.redirectUrl)
    const linkCheckPattern = /\/pages\/link\/[^\/]+\/check/i
    const isXboxForWingdk = this.type?.toLowerCase() === CONSOLE_TYPE.XBOX
      && linkCheckPattern.test(processedRedirectUri || '')

    return isTargetConsoleType || isXboxForWingdk
  }

  async _getIalTicket() {
    try {
      const params = {
        gid: this.gid,
        console_type: this.type,
        console_token: this.token,
        locale: this.locale,
      }

      const { linkTicket, redirectUrl } = await this.infaceAuth.getIalTicket(params) // v2 interface
      if (!linkTicket) {
        throw new InfaceAuthError({ code: 113910, name: 'NOT_DEFINED_IAL_TICKET', message: window.$t('NOT_DEFINED_IAL_TICKET') })
      }

      return { redirectUrl }
    } catch (e) {
      const err = e instanceof InfaceAuthError ? e : new InfaceAuthError({
        code: 113911, name: 'FAIL_TO_GOTO_IAL_PAGE', message: window.$t('FAIL_TO_GOTO_IAL_PAGE'), data: e,
      })
      throw err
    }
  }

  // eslint-disable-next-line class-methods-use-this
  isValidUrl(url) {
    try {
      return new URL(url)
    } catch (err) {
      return false
    }
  }

  async getRedirectUrl() {
    let url

    // v2 PC Toy SDK 에서 issue/link-ticket 호출 후 진입
    if (this.linkTicket && this.redirectUrl) {
      url = this.redirectUrl
    } else {
      // v1 브라우저에서 issue/link-ticket 호출 (하위 호환)
      const { redirectUrl } = await this._getIalTicket()
      url = redirectUrl
    }

    return url
  }

  async gotoIalPage() {
    try {
      let url = await this.getRedirectUrl()
      if (!this.isValidUrl(url)) {
        throw new InfaceAuthError({
          code: 113911, name: 'FAIL_TO_GOTO_IAL_PAGE', message: 'invalid_url', data: url,
        })
      }

      if (this.isCallbackRequired) {
        const failSearchParams = new URLSearchParams({
          response: 'fail',
          code: 113912,
          message: window.$t('FAIL_TO_IAL_CALLBACK'),
        })
        const callbackOk = encodeURIComponent(`${window.location.origin + GW_PATH.REDIRECT}?response=success`)
        const callbackFail = encodeURIComponent(`${window.location.origin + GW_PATH.REDIRECT}?${failSearchParams.toString()}`)

        url += `&success_callback_uri=${callbackOk}&fail_callback_uri=${callbackFail}`
      }

      URI.navigateToUrlWithoutHistory(url)
    } catch (e) {
      const err = e instanceof InfaceAuthError ? e : new InfaceAuthError({
        code: 113911, name: 'FAIL_TO_GOTO_IAL_PAGE', message: window.$t('FAIL_TO_GOTO_IAL_PAGE'), data: e,
      })
      throw err
    }
  }
}
